import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, videos } from '@/mocks/data';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);

  const filteredVideos = useMemo(() => {
    if (!slug) return videos;
    return videos.filter((v) => v.category === slug);
  }, [slug]);

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-10 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900">分类不存在</h1>
          <Link
            to="/"
            className="inline-block mt-4 text-primary-500 hover:text-primary-600"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const canonicalUrl = `https://www.aiqingdao.com/category/${slug}`;
  const pageTitle = `${currentCategory.name} - 爱情岛视频 | 爱情岛视频${currentCategory.name}高清在线观看`;

  const getCategoryName = (catSlug: string) => {
    const cat = categories.find((c) => c.slug === catSlug);
    return cat ? cat.name : '';
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={`爱情岛视频${currentCategory.name}专区，${currentCategory.description}。提供高清爱情岛视频${currentCategory.name}在线观看，免费流畅播放。`}
        keywords={`爱情岛视频${currentCategory.name},爱情岛视频在线观看,爱情岛视频高清,爱情岛视频免费播放`}
        canonicalUrl={canonicalUrl}
      />
      <SchemaOrg type="WebPage" data={{
        name: pageTitle,
        url: canonicalUrl,
        isPartOf: { '@id': 'https://www.aiqingdao.com/#website' },
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.aiqingdao.com/' },
          { '@type': 'ListItem', position: 2, name: currentCategory.name, item: canonicalUrl },
        ],
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[{ label: currentCategory.name }]}
          />

          <section className="w-full px-4 md:px-6 py-8">
            <div className="max-w-[1200px] mx-auto">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-50 rounded-lg">
                  <i className={`${currentCategory.icon} text-xl text-primary-500`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">{currentCategory.description}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-6 mb-8">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              {filteredVideos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                  {filteredVideos.map((video) => (
                    <VideoCard
                      key={video.id}
                      slug={video.slug}
                      title={video.title}
                      thumbnail={video.thumbnail}
                      duration={video.duration}
                      views={video.views}
                      date={video.date}
                      rating={video.rating}
                      category={getCategoryName(video.category)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
                    <i className="ri-video-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">暂无相关内容</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}